import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { SignaturePad } from '../components/SignaturePad';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { CheckCircle2, FileText, Download, ShieldCheck, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v, cur = 'XOF') => {
  if (v === null || v === undefined || v === '') return '—';
  try {
    return new Intl.NumberFormat('fr-FR').format(Number(v)) + ' ' + (cur === 'XOF' ? 'FCFA' : cur);
  } catch {
    return `${v} ${cur}`;
  }
};

export default function MandateSign() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [data, setData] = useState(null);
  const [showPad, setShowPad] = useState(false);
  const [signerName, setSignerName] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [signatureB64, setSignatureB64] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data: r } = await axios.get(`${BACKEND_URL}/api/public/mandate/${token}`);
        if (!mounted) return;
        setData(r);
        setSignerName(r.owner_name || '');
        if (r.already_signed) setDone(true);
      } catch (e) {
        setErr(e?.response?.data?.detail || 'Lien invalide ou expiré');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const handleDownloadPdf = () => {
    window.open(`${BACKEND_URL}/api/public/mandate/${token}/pdf`, '_blank');
  };

  const handleSubmit = async () => {
    if (!signatureB64) {
      toast.error('Veuillez dessiner votre signature');
      return;
    }
    if (!signerName || signerName.trim().length < 3) {
      toast.error('Veuillez saisir votre nom complet');
      return;
    }
    if (!accepted) {
      toast.error('Veuillez cocher « Lu et approuvé »');
      return;
    }
    setSubmitting(true);
    try {
      const { data: r } = await axios.post(
        `${BACKEND_URL}/api/public/mandate/${token}/sign`,
        {
          signature_base64: signatureB64,
          signer_name: signerName.trim(),
          read_and_approved: accepted,
        },
      );
      setDone(true);
      toast.success('Mandat signé avec succès');
      setData((d) => ({ ...d, already_signed: true, signed_at: r.signed_at, signer_name: r.signer_name }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF6EE]">
        <Loader2 className="w-8 h-8 animate-spin text-[#B85C38]" />
      </div>
    );
  }

  if (err) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FBF6EE] p-6">
        <div className="max-w-md w-full bg-white rounded-3xl border border-[#E8D9C0] shadow-xl p-8 text-center">
          <AlertTriangle className="w-14 h-14 mx-auto text-[#B85C38] mb-4" />
          <h1 className="text-2xl font-bold text-[#2B3A2C] mb-2">Lien invalide</h1>
          <p className="text-slate-600">{err}</p>
        </div>
      </div>
    );
  }

  const isRental = !!data?.monthly_rent;
  const price = data?.monthly_rent || data?.sale_price;

  return (
    <div className="min-h-screen bg-[#FBF6EE]" data-testid="mandate-sign-page">
      {/* Header agence */}
      <div className="relative overflow-hidden bg-[#2B3A2C] text-white">
        <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, #B85C38 0%, transparent 40%), radial-gradient(circle at 80% 70%, #D4A745 0%, transparent 40%)'
        }} />
        <div className="relative max-w-4xl mx-auto px-6 py-10">
          <div className="flex items-center gap-4">
            {data?.agency?.logo_url ? (
              <div className="w-16 h-16 rounded-2xl bg-white p-2 ring-2 ring-[#B85C38] flex items-center justify-center">
                <img
                  src={data.agency.logo_url.startsWith('http') ? data.agency.logo_url : `${BACKEND_URL}${data.agency.logo_url}`}
                  alt="Logo agence"
                  className="max-w-full max-h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-16 h-16 rounded-2xl bg-[#B85C38] flex items-center justify-center">
                <FileText className="w-8 h-8 text-white" />
              </div>
            )}
            <div>
              <div className="text-xs tracking-widest opacity-70 uppercase">Signature électronique</div>
              <h1 className="text-3xl font-bold" style={{ fontFamily: 'Clash Display, Outfit, sans-serif' }}>
                Mandat de Gestion
              </h1>
              <div className="text-sm opacity-80">{data?.agency?.name}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 -mt-6 relative z-10 pb-16">
        {/* Success state */}
        {done && (
          <div className="bg-white rounded-3xl border-2 border-[#2B3A2C] shadow-xl p-8 text-center" data-testid="mandate-signed-success">
            <div className="w-20 h-20 mx-auto rounded-full bg-[#2B3A2C] flex items-center justify-center mb-4">
              <CheckCircle2 className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-[#2B3A2C] mb-2">Mandat signé avec succès</h2>
            <p className="text-slate-600 mb-6">
              Merci {data?.signer_name || signerName}, votre signature a bien été enregistrée.
              {data?.signed_at && <> <br />Le {new Date(data.signed_at).toLocaleString('fr-FR')}.</>}
            </p>
            <Button
              onClick={handleDownloadPdf}
              className="bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-full px-6"
              data-testid="mandate-download-signed-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger le mandat signé
            </Button>
          </div>
        )}

        {/* Form state */}
        {!done && (
          <>
            {/* Récapitulatif */}
            <div className="bg-white rounded-3xl border border-[#E8D9C0] shadow-lg p-6 mb-6">
              <h2 className="text-lg font-bold text-[#2B3A2C] mb-4 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5 text-[#B85C38]" />
                Récapitulatif du mandat
              </h2>
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Bien</div>
                  <div className="font-semibold text-[#2B3A2C]">{data?.property_title || '—'}</div>
                  <div className="text-slate-600">{data?.property_address || ''}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Mandant</div>
                  <div className="font-semibold text-[#2B3A2C]">{data?.owner_name || '—'}</div>
                  <div className="text-slate-600">{data?.owner_phone || ''}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">
                    {isRental ? 'Loyer mensuel' : 'Prix de vente'}
                  </div>
                  <div className="font-semibold text-[#B85C38]">{fmt(price, data?.currency)}</div>
                </div>
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500">Type / Durée</div>
                  <div className="font-semibold text-[#2B3A2C] capitalize">
                    {(data?.mandate_type || 'simple').replace('_', ' ')}
                    {data?.mandate_duration_months ? ` · ${data.mandate_duration_months} mois` : ''}
                  </div>
                </div>
                {data?.commission_rate ? (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">Commission agence</div>
                    <div className="font-semibold text-[#2B3A2C]">{data.commission_rate}%</div>
                  </div>
                ) : null}
                {data?.mandate_entry_commission_rate ? (
                  <div>
                    <div className="text-xs uppercase tracking-wide text-slate-500">Commission d'entrée</div>
                    <div className="font-semibold text-[#2B3A2C]">{data.mandate_entry_commission_rate}%</div>
                  </div>
                ) : null}
              </div>
              <div className="mt-5">
                <Button
                  variant="outline"
                  onClick={handleDownloadPdf}
                  className="border-[#2B3A2C] text-[#2B3A2C] hover:bg-[#2B3A2C] hover:text-white rounded-full"
                  data-testid="mandate-preview-btn"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Lire le mandat complet (PDF)
                </Button>
              </div>
            </div>

            {/* Signature */}
            <div className="bg-white rounded-3xl border border-[#E8D9C0] shadow-lg p-6">
              <h2 className="text-lg font-bold text-[#2B3A2C] mb-4">Votre signature</h2>

              <div className="mb-4">
                <label className="text-xs uppercase tracking-wide text-slate-500 mb-1 block">
                  Nom complet du signataire
                </label>
                <Input
                  value={signerName}
                  onChange={(e) => setSignerName(e.target.value)}
                  placeholder="Prénom NOM"
                  className="rounded-xl border-[#E8D9C0]"
                  data-testid="mandate-signer-name"
                />
              </div>

              {!signatureB64 && !showPad && (
                <Button
                  onClick={() => setShowPad(true)}
                  className="bg-[#2B3A2C] hover:bg-[#1e2a20] text-white rounded-full w-full py-6 text-base"
                  data-testid="mandate-open-pad-btn"
                >
                  Signer maintenant
                </Button>
              )}

              {showPad && !signatureB64 && (
                <div className="border-2 border-dashed border-[#B85C38]/40 rounded-2xl p-4 bg-[#FBF6EE]">
                  <SignaturePad
                    width={520}
                    height={180}
                    onSave={(b64) => {
                      setSignatureB64(b64);
                      setShowPad(false);
                    }}
                    onCancel={() => setShowPad(false)}
                  />
                </div>
              )}

              {signatureB64 && (
                <div className="border border-[#2B3A2C]/20 rounded-2xl p-4 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs uppercase tracking-wide text-slate-500">Signature capturée</div>
                    <button
                      onClick={() => { setSignatureB64(''); setShowPad(true); }}
                      className="text-xs text-[#B85C38] hover:underline"
                      data-testid="mandate-resign-btn"
                    >
                      Recommencer
                    </button>
                  </div>
                  <img src={signatureB64} alt="Signature" className="max-h-32 mx-auto" />
                </div>
              )}

              {/* Lu et approuvé */}
              <label className="flex items-start gap-3 mt-5 cursor-pointer p-3 rounded-xl bg-[#FBF6EE] border border-[#E8D9C0]">
                <input
                  type="checkbox"
                  checked={accepted}
                  onChange={(e) => setAccepted(e.target.checked)}
                  className="mt-1 w-5 h-5 accent-[#2B3A2C]"
                  data-testid="mandate-accept-checkbox"
                />
                <span className="text-sm text-slate-700">
                  <b>Lu et approuvé</b> — Je reconnais avoir lu l'intégralité du présent mandat de gestion
                  et j'en accepte l'ensemble des termes. Ma signature électronique a la même valeur qu'une
                  signature manuscrite.
                </span>
              </label>

              <Button
                onClick={handleSubmit}
                disabled={submitting || !signatureB64 || !accepted || (signerName || '').trim().length < 3}
                className="mt-5 bg-[#B85C38] hover:bg-[#a04d2f] text-white rounded-full w-full py-6 text-base disabled:opacity-50"
                data-testid="mandate-submit-btn"
              >
                {submitting ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Envoi…</>
                ) : (
                  <>Valider et signer le mandat</>
                )}
              </Button>

              <p className="text-xs text-slate-500 text-center mt-4">
                Votre signature, la date et votre adresse IP seront enregistrées comme preuve légale.
              </p>
            </div>
          </>
        )}

        <div className="text-center text-xs text-slate-500 mt-6">
          Document électronique généré par {data?.agency?.name || 'GAB\'S SERVICES HABITAT'}
        </div>
      </div>
    </div>
  );
}
